import { useState } from 'react';
import type { GetStaticPropsContext } from 'next';
import { useRouter } from 'next/router';
import ReCAPTCHA from 'react-google-recaptcha';
import { z } from 'zod';

import { Box, Icon } from '@chakra-ui/react';

import { Alert, AlertMessage } from '~/components/alert';
import { LoadingOverlay } from '~/components/loading-overlay';
import { TemplateForm } from '~/components/template-form';
import { PageCategory } from '~/features/analytics/constants';
import { useLeadFormEvent } from '~/features/analytics/hooks/use-lead-form-event';
import { postPersonalizedDiscountForm } from '~/features/discount/controllers/post-personalized-discount-form';
import { addCouponCookie } from '~/features/discount/utils/add-coupon-cookie';
import { publicEnv } from '~/features/environment/public';
import { TagIcon } from '~/features/experiments/ab_plp_redesign_2025/icons';
import { DEFAULT_LANGUAGE } from '~/features/internationalization/languages';
import { PageHead } from '~/features/page/components/page-head';
import { PageLayout } from '~/features/page/components/page-layout';
import { getPage } from '~/features/page/controllers/get-page';
import { Page } from '~/features/page/models/page';
import { TemplateForm as TemplateFormType } from '~/features/page-template/models/template-form';
import { DiscountCardLayout } from '~/features/personalized-discount/components/discount-card-layout';
import { phoneNumberSchema } from '~/features/phone-number/models';
import { postSignup } from '~/features/quick-authentication/controllers/post-signup';
import { QuickAuthenticationError } from '~/features/quick-authentication/errors';
import { useUser } from '~/features/user/hooks/use-user';
import { transformFormDataToEventProperties } from '~/utils/transform-form-data-to-event-properties';

const formFieldDefinitions = [
  {
    id: '1',
    order: 0,
    required: true,
    label: 'What is your employment status?',
    inputType: 'select',
    selectOptions: 'Full Time | Part Time or Contract | Self Employed | Unemployed | Student | Other',
    placeholder: 'Select an option',
    name: 'employmentStatus',
    conditional: '',
  },
  {
    id: '2',
    order: 1,
    required: true,
    name: 'income',
    label: 'What was your total personal income for the most recent reporting period?',
    inputType: 'select',
    selectOptions:
      '$0 - $11,999 | $12,000 - $24,999 | $25,000 - $49,999 | $50,000 - $74,999 | $75,000 - $99,999 | $100,000 - $199,999 | $200,000+ | Prefer not to disclose',
    placeholder: 'Select an option',
  },
  {
    id: '3',
    order: 2,
    required: true,
    label: 'What is your email address?',
    inputType: 'email',
    placeholder: '',
    name: 'email',
  },
  {
    id: '4',
    order: 3,
    required: false,
    name: 'textOffer',
    label: 'Text me my offer (optional)',
    inputType: 'checkbox',
    conditional: '',
  },
  {
    id: '5',
    order: 4,
    required: false,
    name: 'phoneNumber',
    label: 'What is your phone number?',
    inputType: 'tel',
    conditional: 'textOffer',
    placeholder: '',
  },
] as const;

const formTemplate: TemplateFormType = {
  _id: 'personalized-offer-form',
  _type: 'form',
  formName: 'Personalized Offer',
  formData: JSON.stringify(formFieldDefinitions),
  submitButtonText: 'Get Offer',
  cancelButtonText: 'Cancel',
  submitButton: null,
  cancelButton: null,
  sendToSalesforce: false,
  legalText:
    'By submitting this form, you agree to our [Terms of Use](https://www.udacity.com/legal/terms-of-use) and [Privacy Policy](https://www.udacity.com/legal/privacy).',
} as const;

const personalizedDiscountFormSchema = z.object({
  employmentStatus: z.object({
    label: z.string(),
    value: z.string(),
  }),
  income: z.object({
    label: z.string(),
    value: z.string(),
  }),
  email: z.string().email(),
  textOffer: z.boolean(),
  phoneNumber: phoneNumberSchema.optional(),
});

export default function PersonalizedDiscount({ page }: { page: Page }) {
  const router = useRouter();
  const { user, userId } = useUser();
  const [isLoading, setIsLoading] = useState(false);
  const [formError, setFormError] = useState<AlertMessage | undefined>();
  const [isCaptchaRequired, setIsCaptchaRequired] = useState(false);
  const [captchaValue, setCaptchaValue] = useState<string>();
  const { isSubmitting, sendSegmentLeadFormEvents } = useLeadFormEvent();

  async function handlePersonalizedDiscountForm({
    formData,
  }: {
    templateForm: TemplateFormType;
    formData: Record<string, string | { label: string; value: string }>;
  }) {
    setIsLoading(true);
    setFormError(undefined);
    setIsCaptchaRequired(false);

    const personalizedDiscountFormData = personalizedDiscountFormSchema.parse(formData);

    // If user isn't authenticated then we go through the quick auth flow
    if (!userId) {
      try {
        await postSignup(personalizedDiscountFormData.email, captchaValue);
      } catch (error) {
        if (error instanceof QuickAuthenticationError) {
          switch (error.name) {
            // User already has an account, take them to login and redirect back to this page
            case 'EMAIL_EXISTS':
              window.location.href = `${publicEnv.NEXT_PUBLIC_AUTH_URL}/sign-in?next=${window.location.href}`;
              setFormError({
                message: 'An account with this email already exists, please login to continue.',
                level: 'error',
              });
              break;
            case 'CAPTCHA_REQUIRED':
              setFormError({ message: error.message, level: 'warning' });
              setIsCaptchaRequired(true);
              break;
            default:
              setFormError({ message: 'An error occurred signing up. Please try again.', level: 'error' });
          }
        }
        setIsLoading(false);
      }
    }
    try {
      const couponResponse = await postPersonalizedDiscountForm(personalizedDiscountFormData);

      addCouponCookie(couponResponse.couponCode, {
        expires: new Date(couponResponse.expiresAt),
      });

      const params = new URLSearchParams({
        couponCode: encodeURIComponent(couponResponse.couponCode),
        percentDiscount: encodeURIComponent(couponResponse.percentDiscount),
        expiresAt: encodeURIComponent(couponResponse.expiresAt),
      }).toString();

      await sendSegmentLeadFormEvents({
        identifyTraits: {
          email: formData.email,
          phone: formData.phoneNumber,
        },
        trackAnalyticsEvent: {
          name: 'Marketing Qualified Lead Form Submitted',
          type: 'submit',
          label: 'Personalized Discount',
          action: 'form_submitted',
          additionalAttributes: transformFormDataToEventProperties(formData),
        },
        integrations: {
          sendToSalesforce: true,
        },
      });

      router.push(`/personalized-discount-success?${params}`);
    } catch (error) {
      setFormError({ message: 'An error occurred signing up. Please try again.', level: 'error' });
    }
  }

  return (
    <>
      <PageHead page={page} pageCategory={PageCategory.PersonalizedDiscount} />
      <PageLayout bg='grey.50' isSimpleMode>
        <DiscountCardLayout
          headerProps={{
            icon: <Icon as={TagIcon} boxSize='18px' color='white' />,
            preTitle: 'Personalized Discount',
            title: 'Get your personalized offer',
            subtitle: 'Answer just three questions and save!',
          }}
        >
          {formError && <Alert status={formError.level}>{formError.message}</Alert>}

          {isCaptchaRequired && (
            <Box marginTop='16px'>
              <ReCAPTCHA
                sitekey={publicEnv.NEXT_PUBLIC_USER_API_RECAPTCHA_KEY}
                size='normal'
                onChange={(value) => {
                  if (value) {
                    setCaptchaValue(value);
                  }
                }}
              />
            </Box>
          )}

          <Box marginTop={formError || isCaptchaRequired ? '24px' : undefined} position='relative'>
            {isLoading && <LoadingOverlay message='Creating your offer...' />}
            <TemplateForm
              key={user?.email}
              defaultValues={{ email: user?.email || '' }}
              inputStyleProps={{ borderColor: 'grey.500' }}
              isFullWidth={true}
              templateForm={formTemplate}
              submitButtonProps={{
                isLoading: isSubmitting,
                bg: 'blue.500',
                borderRadius: '4px',
                color: 'white',
                fontSize: '15px',
                fontWeight: 500,
                padding: '14px 24px',
                _hover: { bg: 'blue.700' },
              }}
              onCancelClick={router.back}
              onSubmit={handlePersonalizedDiscountForm}
            />
          </Box>
        </DiscountCardLayout>
      </PageLayout>
    </>
  );
}

export async function getStaticProps({ locale }: GetStaticPropsContext) {
  if (locale !== DEFAULT_LANGUAGE) {
    return {
      notFound: true,
    };
  }

  const page = await getPage('/personalized-discount');

  if (!page) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      page,
    },
  };
}
